import { ORGANIZATION_INFO_UPDATE_MUTATION } from 'apollo/mutations/organization-info-mutations';
import { ExecutionResult } from 'graphql';
import { useApolloUpdate } from 'hooks';
import { useDispatch } from 'react-redux';
import { IOrganizationInfo, IOrganizationInfoUpdateInput } from 'types/OrganizationInfo';
import useOrganization from '../../store/useOrganization';

interface IMutationResult {
  updateOrganizationInfo: IOrganizationInfo;
}

type OrganizationSetSettingsTuple = [
  <Key extends keyof IOrganizationInfoUpdateInput>(
    key: Key,
    value: IOrganizationInfoUpdateInput[Key]
  ) => Promise<ExecutionResult<IMutationResult>>,
  (value: Omit<IOrganizationInfoUpdateInput, 'id'>) => Promise<ExecutionResult<IMutationResult>>
];

export default function useOrganizationInfoUpdate(): OrganizationSetSettingsTuple {
  const [updateKey, update] = useApolloUpdate<IOrganizationInfoUpdateInput, IMutationResult>(
    ORGANIZATION_INFO_UPDATE_MUTATION,
    'organizationInfo'
  );

  const organization = useOrganization();
  const organizationInfo = organization.organizationInfo!;

  const dispatch = useDispatch();

  async function updateOrganizationInfo(value: Omit<IOrganizationInfoUpdateInput, 'id'>) {
    const result = await update({ ...value, id: organizationInfo.id });
    dispatch({
      type: 'ORGANIZATION_SET',
      payload: { ...organization, organizationInfo: result.data!.updateOrganizationInfo },
    });
    return result;
  }

  async function updateOrganizationInfoKey<
    Key extends keyof IOrganizationInfoUpdateInput,
    Value extends IOrganizationInfoUpdateInput[Key]
  >(key: Key, value: Value) {
    const result = await updateKey(organizationInfo.id, key, value);
    dispatch({
      type: 'ORGANIZATION_SET',
      payload: { ...organization, organizationInfo: result.data!.updateOrganizationInfo },
    });
    return result;
  }

  return [updateOrganizationInfoKey, updateOrganizationInfo];
}
